import * as THREE from 'three/webgpu';

import {
    resolveGlbDatumSlotFrame,
    type GlbDatumSlotTargetDescriptor,
} from '../../imported-model/datum-slot';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    buildGlbDatumSlotMeasureObjectId,
    MeasureUnboundedGeometryKind,
    resolveGlbDatumSlotDisplayName,
    type MeasureUnboundedGeometryBinding,
} from '../types/measure-unbounded-types';

/**
 * 无边界测量几何解析结果。
 */
export interface MeasureUnboundedGeometryResolveResult {
    /** 无边界绑定。 */
    binding: MeasureUnboundedGeometryBinding;
    /** 默认世界拾取点。 */
    worldPickPoint: THREE.Vector3;
}

/**
 * 无边界测量几何解析器。
 *
 * 职责：将 datum-slot / 正式基准解析为测量 SSOT 可用的无边界绑定。
 */
export class MeasureUnboundedGeometryResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 从 GLB datum-slot 描述符构建无边界测量绑定。
     *
     * @param descriptor datum-slot 描述符。
     * @returns 解析结果；frame 无法解析时返回 null。
     */
    public resolveGlbDatumSlot(
        descriptor: GlbDatumSlotTargetDescriptor,
    ): MeasureUnboundedGeometryResolveResult | null {
        const frame = resolveGlbDatumSlotFrame(this.viewer.scene, descriptor);
        if (!frame) {
            return null;
        }

        const isOrigin = descriptor.slotKind === 'origin';
        const binding: MeasureUnboundedGeometryBinding = {
            kind: isOrigin
                ? MeasureUnboundedGeometryKind.GlbDatumSlotOrigin
                : MeasureUnboundedGeometryKind.GlbDatumSlotPlane,
            measureObjectId: buildGlbDatumSlotMeasureObjectId(descriptor),
            displayName: resolveGlbDatumSlotDisplayName(descriptor.slotKind),
            datumSlot: {
                tabId: descriptor.tabId,
                hostObjectId: descriptor.hostObjectId,
                slotKind: descriptor.slotKind,
            },
        };

        return {
            binding,
            worldPickPoint: frame.origin.clone(),
        };
    }

    /**
     * 从正式基准场景对象构建无边界绑定。
     *
     * @param sceneObject 场景对象。
     * @returns 解析结果；非正式基准时返回 null。
     */
    public resolveFormalDatumSceneObject(
        sceneObject: THREE.Object3D,
    ): MeasureUnboundedGeometryResolveResult | null {
        if (isDatumPlaneObject(sceneObject)) {
            return this.resolveFormalDatumPlane(sceneObject);
        }

        if (isDatumAxis(sceneObject)) {
            return this.resolveFormalDatumAxis(sceneObject);
        }

        if (isDatumPoint(sceneObject)) {
            return this.resolveFormalDatumPoint(sceneObject);
        }

        return null;
    }

    /**
     * 将无边界绑定写入测量实体引用。
     *
     * @param binding 无边界绑定。
     * @param worldPickPoint 世界拾取点。
     * @returns 测量实体引用。
     */
    public toMeasureEntityRef(
        binding: MeasureUnboundedGeometryBinding,
        worldPickPoint: THREE.Vector3,
    ): MeasureEntityRef {
        return {
            objectId: binding.measureObjectId,
            worldPickPoint: worldPickPoint.clone(),
            unboundedBinding: binding,
        };
    }

    /**
     * 解析正式基准面绑定。
     *
     * @param sceneObject 基准面对象。
     * @returns 解析结果。
     */
    private resolveFormalDatumPlane(
        sceneObject: THREE.Object3D,
    ): MeasureUnboundedGeometryResolveResult | null {
        const matchedPlane = this.viewer.getAllDatumPlanes()
            .find((plane) => plane.getObject3D().uuid === sceneObject.uuid);
        if (!matchedPlane) {
            return null;
        }

        const worldPickPoint = this.resolveDatumPlaneReferencePoint(sceneObject)
            ?? sceneObject.getWorldPosition(new THREE.Vector3());

        return {
            binding: {
                kind: MeasureUnboundedGeometryKind.FormalDatumPlane,
                measureObjectId: sceneObject.uuid,
                displayName: matchedPlane.name?.trim() || '基准面',
                datumPlaneId: matchedPlane.id,
            },
            worldPickPoint,
        };
    }

    /**
     * 解析正式基准轴绑定。
     *
     * @param sceneObject 基准轴对象。
     * @returns 解析结果。
     */
    private resolveFormalDatumAxis(
        sceneObject: THREE.Object3D,
    ): MeasureUnboundedGeometryResolveResult | null {
        if (!isDatumAxis(sceneObject)) {
            return null;
        }

        const matchedAxis = (this.viewer.datumAxisManager?.getAllAxes() ?? [])
            .find((axis) => axis.getObject3D().uuid === sceneObject.uuid);
        if (!matchedAxis) {
            return null;
        }

        const midpoint = sceneObject.getWorldMidpoint();
        const worldPickPoint = midpoint ?? sceneObject.originWorld.clone();

        return {
            binding: {
                kind: MeasureUnboundedGeometryKind.FormalDatumAxis,
                measureObjectId: sceneObject.uuid,
                displayName: matchedAxis.name?.trim() || '基准轴',
                datumAxisId: matchedAxis.id,
            },
            worldPickPoint,
        };
    }

    /**
     * 解析正式基准点绑定。
     *
     * @param sceneObject 基准点对象。
     * @returns 解析结果。
     */
    private resolveFormalDatumPoint(
        sceneObject: THREE.Object3D,
    ): MeasureUnboundedGeometryResolveResult | null {
        if (!isDatumPoint(sceneObject)) {
            return null;
        }

        const matchedPoint = (this.viewer.datumPointManager?.getAllPoints() ?? [])
            .find((point) => point.getObject3D().uuid === sceneObject.uuid);
        if (!matchedPoint) {
            return null;
        }

        return {
            binding: {
                kind: MeasureUnboundedGeometryKind.FormalDatumPoint,
                measureObjectId: sceneObject.uuid,
                displayName: matchedPoint.name?.trim() || '基准点',
                datumPointId: matchedPoint.id,
            },
            worldPickPoint: sceneObject.getWorldPoint().clone(),
        };
    }

    /**
     * 解析基准面层级中的平面参考点。
     *
     * @param planeObject 基准面对象。
     * @returns 世界坐标参考点。
     */
    private resolveDatumPlaneReferencePoint(planeObject: THREE.Object3D): THREE.Vector3 | null {
        let referencePlane: THREE.Plane | null = null;

        planeObject.traverse((childObject) => {
            if (referencePlane) {
                return;
            }

            const candidate = Reflect.get(childObject, 'referencePlane');
            if (candidate instanceof THREE.Plane) {
                referencePlane = candidate;
            }
        });

        if (!referencePlane) {
            return null;
        }

        const point = referencePlane.coplanarPoint(new THREE.Vector3());
        planeObject.localToWorld(point);
        return point;
    }
}
